// src/components/HeroBanner/AjouterHeroBanner.js
import React, { useState, useCallback, useEffect } from "react";
import validator from "validator";
import { Button, Card, Form, Container, Row, Col } from "react-bootstrap";
import { useParams, useHistory } from "react-router-dom";
import { useDispatch } from "react-redux";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useDropzone } from "react-dropzone";
import {
  addHeroBanner,
  editHeroBanner,
  getHeroBannerById,
  uploadHeroBannerImage,
} from "../../../Redux/heroBannerReduce";

function AjouterHeroBanner() {
  const notify = (type, msg) => {
    if (type === 1)
      toast.success(
        <strong>
          <i className="fas fa-check-circle"></i>
          {msg}
        </strong>,
      );
    else
      toast.error(
        <strong>
          <i className="fas fa-exclamation-circle"></i>
          {msg}
        </strong>,
      );
  };

  const dispatch = useDispatch();
  const navigate = useHistory();
  const { id } = useParams();

  // Form states
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [image, setImage] = useState("");
  const [preview, setPreview] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);

  // Load hero banner data if editing
  useEffect(() => {
    if (id) {
      dispatch(getHeroBannerById(id)).then((response) => {
        const heroBanner = response.payload;
        setName(heroBanner?.name || "");
        setDescription(heroBanner?.description || "");
        setImage(heroBanner?.img || "");
        setPreview(heroBanner?.img || null);
      });
    }
  }, [dispatch, id]);

  function listeHeroBanner() {
    navigate.push("/heroBanner/list");
  }

  const onDrop = useCallback((acceptedFiles) => {
    if (acceptedFiles.length === 0) return;
    if (acceptedFiles.length > 1) {
      notify(2, "Veuillez sélectionner une seule image");
      return;
    }
    const file = acceptedFiles[0];
    setImageFile(file);
    setPreview(URL.createObjectURL(file));

    // Upload via reducer
    uploadFile(file);
  }, []);

  const uploadFile = async (file) => {
    setIsUploading(true);
    try {
      const formData = new FormData();
      formData.append("image", file);

      // Dispatch upload action
      const result = await dispatch(uploadHeroBannerImage(formData));

      if (result.payload && result.payload.url) {
        setImage(result.payload.url);
        notify(1, "Image téléchargée avec succès !");
      } else {
        throw new Error("Échec du téléchargement de l'image");
      }
    } catch (error) {
      console.error("Erreur de téléchargement :", error);
      notify(2, "Échec du téléchargement de l'image. Veuillez réessayer.");
      setPreview(null);
    } finally {
      setIsUploading(false);
    }
  };

  // FIXED: Correct accept prop syntax
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      "image/jpeg": [".jpeg", ".jpg"],
      "image/png": [".png"],
    },
    maxFiles: 1,
    disabled: isUploading,
  });

  async function submitForm(event) {
    event.preventDefault();

    const required = document.getElementsByClassName("required");
    let isValid = true;

    // Clear previous errors
    document.querySelectorAll(".error").forEach((el) => (el.innerHTML = ""));
    document
      .querySelectorAll(".required")
      .forEach((el) => (el.style.borderColor = "#ccc"));

    // Validate required fields
    for (let i = 0; i < required.length; i++) {
      if (validator.isEmpty(required[i].value)) {
        required[i].style.borderColor = "red";
        document.getElementsByClassName("error")[i].innerHTML =
          required[i].name + " est obligatoire";
        notify(2, required[i].name + " doit être non vide");
        isValid = false;
      }
    }

    // Validate image
    if (image === "") {
      notify(2, "Image est requise");
      isValid = false;
    }

    if (isValid) {
      const heroBannerData = {
        name,
        description,
        img: image,
      };

      try {
        let result;
        if (id) {
          result = await dispatch(editHeroBanner({ id, ...heroBannerData }));
        } else {
          result = await dispatch(addHeroBanner(heroBannerData));
        }

        if (result.payload) {
          notify(1, id ? "Modification avec succès" : "Insertion avec succès");
          setTimeout(() => {
            listeHeroBanner();
          }, 1500);
        }
      } catch (error) {
        notify(2, error.message || "Erreur lors de l'enregistrement");
      }
    }
  }

  return (
    <Container fluid>
      <ToastContainer />
      <div className="section-image">
        <Container>
          <Row>
            <Col md="12">
              <Button
                className="btn-wd btn-outline mr-1 float-left"
                type="button"
                variant="info"
                onClick={listeHeroBanner}
              >
                <span className="btn-label">
                  <i className="fas fa-list"></i>
                </span>
                Retour à la liste
              </Button>
            </Col>
          </Row>
          <Row>
            <Col md="12">
              <Form onSubmit={submitForm}>
                <Card>
                  <Card.Header>
                    <Card.Title as="h4">
                      {!id
                        ? "Ajouter une hero banner"
                        : "Modifier une hero banner"}
                    </Card.Title>
                  </Card.Header>
                  <Card.Body>
                    <Row>
                      <Col md="6">
                        <Form.Group>
                          <label>Nom</label>
                          <Form.Control
                            value={name}
                            placeholder="Nom de la hero banner"
                            name="Nom"
                            type="text"
                            onChange={(e) => setName(e.target.value)}
                          />
                        </Form.Group>
                      </Col>
                      <Col md="6">
                        <Form.Group>
                          <label>Description</label>
                          <Form.Control
                            value={description}
                            placeholder="Description"
                            name="Description"
                            type="text"
                            onChange={(e) => setDescription(e.target.value)}
                          />
                        </Form.Group>
                      </Col>
                    </Row>

                    <Row>
                      <Col md="12">
                        <Form.Group>
                          <label>Image *</label>
                          <div
                            {...getRootProps()}
                            className={`upload-block border-2 border-dashed rounded-3 p-5 text-center cursor-pointer transition-colors
                                ${isDragActive ? "border-primary bg-primary bg-opacity-10" : "border-secondary"}
                                ${isUploading ? "opacity-50 cursor-not-allowed" : ""}`}
                            style={{
                              border: "2px dashed #ccc",
                              borderRadius: "0.3rem",
                              padding: "2rem",
                              textAlign: "center",
                              cursor: "pointer",
                              backgroundColor: isDragActive
                                ? "#f0f8ff"
                                : "white",
                            }}
                          >
                            <input {...getInputProps()} />
                            <p className="text-muted">
                              {isUploading
                                ? "Téléchargement en cours..."
                                : isDragActive
                                  ? "Déposez une image ici"
                                  : "Glissez-déposez une image, ou cliquez pour sélectionner"}
                            </p>
                          </div>
                          {preview && (
                            <div
                              className="mt-4 text-center"
                              style={{ marginTop: "1rem" }}
                            >
                              <img
                                src={preview}
                                alt="Aperçu"
                                className="img-fluid rounded"
                                style={{ height: "200px", objectFit: "cover" }}
                              />
                            </div>
                          )}
                          <Form.Text className="text-muted">
                            Formats acceptés: JPEG, JPG, PNG
                          </Form.Text>
                        </Form.Group>
                      </Col>
                    </Row>

                    <Button
                      className="btn-fill pull-right"
                      type="submit"
                      variant="info"
                      disabled={isUploading}
                    >
                      Enregistrer
                    </Button>
                    <div className="clearfix"></div>
                  </Card.Body>
                </Card>
              </Form>
            </Col>
          </Row>
        </Container>
      </div>
    </Container>
  );
}

export default AjouterHeroBanner;
